import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { ThemeService, ThemeConfig } from '../../services/theme/theme.service';
import { Subscription } from 'rxjs';
import { CustomThemeModalComponent } from '../custom-theme-modal/custom-theme-modal.component';

@Component({
  selector: 'app-floating-theme-selector',
  templateUrl: './floating-theme-selector.component.html',
  styleUrls: ['./floating-theme-selector.component.scss']
})
export class FloatingThemeSelectorComponent implements OnInit, OnDestroy {
  @ViewChild(CustomThemeModalComponent) customThemeModal!: CustomThemeModalComponent;
  
  isOpen = false;
  currentTheme = 'moderno';
  themes: ThemeConfig[] = [];
  private themeSubscription: Subscription = new Subscription();
  
  // Posición flotante (parte inferior)
  position = { x: 50, y: window.innerHeight - 100 };
  isDragging = false;
  private dragStart = { x: 0, y: 0 };
  
  // Dirección del despliegue
  opensUpward = false;

  constructor(private themeService: ThemeService) {}

  ngOnInit(): void {
    this.themes = this.themeService.getAllThemes();
    this.themeSubscription = this.themeService.currentTheme$.subscribe(theme => {
      this.currentTheme = theme;
    });

    // Cargar posición guardada o usar posición por defecto en la parte inferior
    const savedPosition = localStorage.getItem('themeSelectorPosition');
    if (savedPosition) {
      this.position = JSON.parse(savedPosition);
    } else {
      // Posición por defecto en la parte inferior
      this.position = { x: 50, y: window.innerHeight - 100 };
    }
  }

  ngOnDestroy(): void {
    this.themeSubscription.unsubscribe();
    this.savePosition();
  }

  toggleSelector(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.checkOpenDirection();
    }
  }

  private checkOpenDirection(): void {
    const selectorHeight = 60; // Altura del botón selector
    const dropdownHeight = 400; // Altura estimada del dropdown
    const availableSpaceBelow = window.innerHeight - (this.position.y + selectorHeight);
    const availableSpaceAbove = this.position.y;
    
    // Si no hay espacio suficiente hacia abajo, abre hacia arriba
    this.opensUpward = availableSpaceBelow < dropdownHeight && availableSpaceAbove > dropdownHeight;
  }

  selectTheme(themeName: string): void {
    this.themeService.setThemeFromQuery(themeName);
    this.isOpen = false;
  }

  getCurrentThemeDisplayName(): string {
    const theme = this.themeService.getThemeByName(this.currentTheme);
    return theme ? theme.displayName : 'Moderno';
  }

  getCurrentThemeColor(): string {
    const theme = this.themeService.getThemeByName(this.currentTheme);
    return theme ? theme.colors.primary : '#2563eb';
  }

  getThemeDescription(themeName: string): string {
    const descriptions: Record<string, string> = {
      moderno: 'Diseño limpio y contemporáneo',
      corporativo: 'Profesional y formal',
      creativo: 'Colorido y dinámico',
      minimalista: 'Simple y elegante',
      personalizado: 'Tema personalizado con tus colores'
    };
    return descriptions[themeName] || '';
  }

  openCustomThemeModal(): void {
    this.isOpen = false;
    this.customThemeModal.openModal();
  }

  // Funcionalidad de arrastrar
  onMouseDown(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX - this.position.x,
        y: event.clientY - this.position.y
      };
      event.preventDefault();
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.position.x = event.clientX - this.dragStart.x;
      this.position.y = event.clientY - this.dragStart.y;
      
      // Mantener dentro de los límites de la ventana
      const selectorWidth = 280; // ancho del selector
      const selectorHeight = 60; // altura del botón (no del dropdown)
      
      // Límites más permisivos - solo evitar que se salga completamente de la pantalla
      const maxX = window.innerWidth - selectorWidth;
      const maxY = window.innerHeight - selectorHeight;
      
      this.position.x = Math.max(0, Math.min(this.position.x, maxX));
      this.position.y = Math.max(0, Math.min(this.position.y, maxY));
      
      // Recalcular dirección si está abierto
      if (this.isOpen) {
        this.checkOpenDirection();
      }
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isDragging) {
      this.isDragging = false;
      this.savePosition();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    // Recalcular dirección si está abierto
    if (this.isOpen) {
      this.checkOpenDirection();
    }
  }

  private savePosition(): void {
    localStorage.setItem('themeSelectorPosition', JSON.stringify(this.position));
  }

  // Método para mostrar/ocultar el selector
  toggleVisibility(): void {
    const selector = document.querySelector('.floating-theme-selector');
    if (selector) {
      selector.classList.toggle('hidden');
    }
  }
}
